import { useCallback, useState } from 'react';
import { SDKResponse } from '@commercetools/frontend-sdk';
import { ProductQuery } from 'shared/types/query/ProductQuery';
import { ProductPaginatedResult } from 'shared/types/result';
import useSWRInfinite from 'swr/infinite';
import { sdk } from 'sdk';

sdk.defaultConfigure('en');

type PaginatedResponse = { isError?: boolean; data?: ProductPaginatedResult } | null;

/**
 * Shared helper to transform paginated SWR data into a single result
 */
const transformPaginatedData = (data: PaginatedResponse[] | undefined) => {
  const firstPage = data?.[0];
  const lastPage = data?.[data.length - 1];

  const allItems = data?.flatMap((page) => (!page?.isError && page?.data?.items) || []) || [];
  const seenKeys = new Set<string>();
  const uniqueItems = allItems.filter((item) => {
    if (!item.productKey || seenKeys.has(item.productKey)) {
      return false;
    }
    seenKeys.add(item.productKey);
    return true;
  });

  const totalItems = (!firstPage?.isError && firstPage?.data?.total) || 0;
  const facets = (!firstPage?.isError && firstPage?.data?.facets) || [];
  const showMoreThreshold = (!firstPage?.isError && firstPage?.data?.showMoreThreshold) || 0;
  const breadcrumbs = (!firstPage?.isError && firstPage?.data?.breadcrumbs) || [];
  const title = (!firstPage?.isError && firstPage?.data?.title) || '';
  const hasMoreItems = lastPage?.data?.items?.length !== 0 && uniqueItems.length < totalItems;

  return {
    data: {
      items: uniqueItems,
      total: totalItems,
      count: (!lastPage?.isError && lastPage?.data?.count) || 0,
      facets,
      showMoreThreshold,
      breadcrumbs,
      title,
    } as ProductPaginatedResult,
    hasMoreItems,
  };
};

/**
 * Hook for product search functionality with infinite scroll
 * @param query Product query parameters
 * @param initialData Optional server-side pre-fetched first page of products for immediate rendering (LCP improvement)
 * @returns Product search results and methods
 */
export const useProductSearch = (query: ProductQuery, initialData?: ProductPaginatedResult) => {
  // Wrap the server-side data into the SDKResponse shape that SWR expects as its fallbackData.
  const swrFallbackData: SDKResponse<ProductPaginatedResult>[] | undefined = initialData
    ? [{ isError: false, tracing: { frontendRequestId: 'ssr' }, data: initialData }]
    : undefined;

  const { data, size, setSize, isLoading, isValidating, mutate } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData && previousPageData.isError) return null;
      if (previousPageData && (!previousPageData.data?.items || previousPageData.data.items.length === 0)) return null;

      const PAGE_SIZE = 40;
      const offset = pageIndex * PAGE_SIZE;
      return ['/action/product/search', offset, query.categorySlug, query.activeFacets, query.sort];
    },
    async ([, offset]) => {
      return await sdk.composableCommerce.search.productSearch({
        ...query,
        offset,
      });
    },
    {
      keepPreviousData: true,
      // Use server-side pre-fetched data as the initial display value so products render
      // immediately without waiting for the client-side SWR fetch to complete.
      fallbackData: swrFallbackData,
      // Skip the redundant revalidation on mount when SSR data is already fresh.
      // SWR will still fetch when the key changes (filter/sort/pagination interactions).
      revalidateOnMount: !initialData,
    },
  );

  const productsPaginatedResults = transformPaginatedData(data);

  return {
    isLoading,
    productsPaginatedResults,
    hasMoreItems: productsPaginatedResults.hasMoreItems,
    mutate,
    isValidating,
    loadMore: () => {
      setSize(size + 1);
    },
    reset: () => {
      setSize(1);
    },
    currentPage: size,
  };
};

/**
 * Hook for product text search functionality with infinite scroll
 * @param searchInput The search term
 * @param query Product query parameters
 * @param identifier Unique identifier for the search
 * @returns Text search methods and state
 */
export const useTextSearch = (searchInput: string, query: ProductQuery, identifier: string) => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(searchInput);

  const { data, size, setSize, isLoading, isValidating, mutate } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (!searchTerm) return null;
      if (previousPageData && previousPageData.isError) return null;
      if (previousPageData && (!previousPageData.data?.items || previousPageData.data.items.length === 0)) return null;

      const PAGE_SIZE = 40;
      const offset = pageIndex * PAGE_SIZE;

      // SWR key
      return ['/action/search/textSearch', searchTerm, identifier, offset, query?.sort, query?.activeFacets];
    },
    async ([, term, , offset]) => {
      if (!term) return null;
      return await sdk.composableCommerce.search.textSearch(
        { searchString: term as string },
        { ...query, offset: offset as number },
      );
    },
    {
      keepPreviousData: true,
      revalidateAll: true,
      dedupingInterval: 5000,
    },
  );

  const textSearchResults = transformPaginatedData(data);

  /**
   * Search for text with the given string
   * @param term The search term
   * @returns The search results
   */
  const textSearch = useCallback(
    async (newSearchTerm: string) => {
      setSearchTerm(newSearchTerm);
      setSize(1); // Reset to first page when searching
      return mutate();
    },
    [mutate, setSize],
  );

  const clearTextSearch = useCallback(() => {
    setSearchTerm(undefined);
    setSize(1);
  }, [setSize]);

  return {
    textSearch,
    clearTextSearch,
    textSearchLoading: isLoading,
    textSearchResults,
    hasMoreItems: textSearchResults.hasMoreItems,
    textSearchTerm: searchTerm,
    loadMore: () => {
      setSize(size + 1);
    },
    reset: () => {
      setSize(1);
    },
    currentPage: size,
    isValidating,
  };
};
